import React, { useMemo } from 'react';
import { useRouter } from 'next/router';
import { Box } from '@mantine/core';

import { FEATURE } from '~/common/enums/feature.enum';
import repeat from '~/common/utils/repeat';
import { ChannelContests } from '~/domains/channel/components/ChannelContests/ChannelContests';
import { useChannelContests } from '~/domains/channel/components/ChannelContests/useChannelContests';
import { useIsFeatureEnabled } from '~/hooks/useIsFeatureEnabled';
import { useInfiniteScroll } from '~/hooks/useInfiniteScroll';
import { UserContestAssociation } from '~/domains/contest/domains/services/enums/user-contest-association.enum';
import { getInfoAlerts } from '~/domains/contest/domains/services/utils/alertFilters';
import useGetActionBannerInfo from '~/domains/contest/domains/entries/hooks/useGetActionBannerInfo';
import { ActiveTab } from '~/domains/commissioner/enums';
import { ChannelContestFilter } from '~/domains/channel/enums';
import getIsEnteredTabShown from '~/domains/channel/utils/getIsEnteredTabShown';
import { URLQueryKeys } from '~/domains/common/types/URLQueryKeys';

type ChannelPageContestsProps = {
  isCurrentUserChannel: boolean;
  userId: string;
  isFullWidth?: boolean;
  isSplashAccount?: boolean;
  isCommissionerHub?: boolean;
};

export function ConnectedChannelContests({
  isCurrentUserChannel,
  isFullWidth,
  userId,
  isSplashAccount,
  isCommissionerHub,
}: ChannelPageContestsProps) {
  const router = useRouter();

  const isManageContestEnabled = useIsFeatureEnabled(FEATURE.ENABLE_MANAGE_CONTEST);
  const { getActionBannerInfo } = useGetActionBannerInfo();
  const handle = router.query.handle;
  const activeTab = (router.query[URLQueryKeys.ACTIVE_TAB] as ActiveTab) || ActiveTab.ACTIVE;

  const isEnteredTabShown = getIsEnteredTabShown({ isCurrentUserChannel, isSplashAccount });

  const association = useMemo(
    () =>
      isEnteredTabShown
        ? (router.query[URLQueryKeys.ASSOCIATION] as UserContestAssociation) ||
          UserContestAssociation.CREATED
        : UserContestAssociation.CREATED,
    [isEnteredTabShown, router.query]
  );

  const channelContestFilter = useMemo(
    () =>
      (router.query[URLQueryKeys.CONTEST_FILTER] as ChannelContestFilter) ||
      (isCurrentUserChannel ? ChannelContestFilter.ALL : ChannelContestFilter.NOT_YET_STARTED),
    [isCurrentUserChannel, router.query]
  );

  const { queryKey, contests, fetchNextPage, hasNextPage, isLoading, isFetching } =
    useChannelContests({
      userId,
      activeTab,
      channelContestFilter,
      isCommissionerHub,
      association,
    });

  const { loaderRef } = useInfiniteScroll({
    hasMore: hasNextPage,
    fetchMore: fetchNextPage,
  });

  const isFinalView = isCommissionerHub
    ? activeTab === ActiveTab.FINAL
    : channelContestFilter === ChannelContestFilter.FINAL;

  if (isLoading || isFetching) {
    return (
      <ChannelContests isFullWidth={isFullWidth}>
        {repeat(5, (index) => (
          <ChannelContests.Skeleton key={index} />
        ))}
      </ChannelContests>
    );
  }

  if (contests?.length) {
    return (
      <ChannelContests isFullWidth={isFullWidth} key={queryKey.join('-')}>
        {contests.map((contest, index) => (
          <ChannelContests.ContestCard
            // eslint-disable-next-line react/no-array-index-key
            key={`${queryKey.join('-')}-${contest.id}-${index}`}
            contest={contest}
            isMyContestsView={
              isCurrentUserChannel && association === UserContestAssociation.CREATED
            }
            isManageContestEnabled={
              isCurrentUserChannel &&
              isManageContestEnabled &&
              association === UserContestAssociation.CREATED
            }
            isShareContestEnabled={
              !contest.is_after_first_late_swap_deadline && Boolean(contest.share_url)
            }
            // Commissioner-only: render display-only info alerts (e.g. contest_restarted)
            // as the gray banner. Service-fee alerts keep their own renderer.
            actionBanner={
              isCurrentUserChannel && association === UserContestAssociation.CREATED
                ? getActionBannerInfo({ values: getInfoAlerts(contest.alerts) })
                : undefined
            }
            isContestThumbnailShown
          />
        ))}
        {hasNextPage && !isLoading && contests.length && (
          <>
            {repeat(2, (index) => (
              <ChannelContests.Skeleton key={index} />
            ))}
            <div ref={loaderRef} />
          </>
        )}
      </ChannelContests>
    );
  }

  return (
    <Box maw="450px" mx="auto">
      <ChannelContests.EmptyState
        isFinalView={isFinalView}
        association={association}
        isCurrentUserChannel={isCurrentUserChannel}
        handle={handle as string}
        userId={userId}
      />
    </Box>
  );
}
